// Kirish (Q-021): restoran logini va paroli, xodimlarning parol va PIN bloklari.
// Login bilan har bir qurilma bir marta kiradi; xodim PIN (+ shaxsiy parol) bilan aniqlanadi.
import { useState } from 'react';
import { Alert, Badge, Button, Card, EmptyState, Icon, Modal, Secret, Spinner, TextField, useAction, useAsync } from '@onepos/ui';
import { api } from '../../lib/api';
import { useMe } from '../../lib/auth';
import { fmtDate, fmtDateTime } from '../../lib/format';
import type { TenantDetail } from '../../lib/types';

interface LoginInfo {
  login: string | null;
  password_set_at: string | null;
  locked_until: string | null;
  staff: {
    id: string;
    full_name: string;
    role_name: string;
    role_locked: boolean;
    is_active: boolean;
    has_password: boolean;
    password_required: boolean;
    password_locked_until: string | null;
    pin_locked_until: string | null;
  }[];
}

function readablePassword(len = 10): string {
  const alpha = 'abcdefghjkmnpqrstuvwxyz23456789';
  const bytes = crypto.getRandomValues(new Uint8Array(len));
  return Array.from(bytes, (b) => alpha[b % alpha.length]).join('');
}

export function LoginTab({ data }: { data: TenantDetail }) {
  const me = useMe();
  const canEdit = me.role !== 'billing';
  const { data: info, error, loading, reload } = useAsync(() => api.rpc<LoginInfo>('platform_tenant_login', { p_tenant: data.tenant.id }), [data.tenant.id]);
  const [editOpen, setEditOpen] = useState(false);
  const [clearFor, setClearFor] = useState<LoginInfo['staff'][number] | null>(null);
  const [pinFor, setPinFor] = useState<LoginInfo['staff'][number] | null>(null);
  const [newPin, setNewPin] = useState<string | null>(null);
  const pinAct = useAction(async (staff: string) => {
    const r = await api.rpc<{ pin: string }>('platform_staff_pin', { p_tenant: data.tenant.id, p_staff: staff });
    setNewPin(r.pin);
    reload();
  });
  const act = useAction(async (staff: string, action: 'unlock' | 'clear') => {
    await api.rpc('platform_staff_password', { p_tenant: data.tenant.id, p_staff: staff, p_action: action });
    setClearFor(null);
    reload();
  });
  const unlockLogin = useAction(async () => {
    if (!info?.login) return;
    await api.rpc('platform_tenant_login_set', { p_tenant: data.tenant.id, p_login: info.login, p_password: null });
    reload();
  });

  if (loading && !info) return <Spinner />;
  if (error) return <Alert tone="danger">{error.message}</Alert>;
  if (!info) return null;

  return (
    <>
      <Card
        title="Restoran logini"
        actions={
          canEdit && (
            <Button size="sm" variant={info.login ? 'secondary' : 'primary'} onClick={() => setEditOpen(true)}>
              <Icon name="key" /> {info.login ? 'Login yoki parolni almashtirish' : 'Login berish'}
            </Button>
          )
        }
      >
        {info.login ? (
          <dl className="op-dl">
            <div className="op-dl__row">
              <dt>Login</dt>
              <dd>
                <b>{info.login}</b>
              </dd>
            </div>
            <div className="op-dl__row">
              <dt>Parol berilgan</dt>
              <dd>{fmtDate(info.password_set_at)}</dd>
            </div>
            {info.locked_until && (
              <div className="op-dl__row">
                <dt>Holat</dt>
                <dd>
                  <Badge tone="danger">{fmtDateTime(info.locked_until)} gacha bloklangan</Badge>{' '}
                  {canEdit && (
                    <Button size="sm" variant="ghost" disabled={unlockLogin.busy} onClick={() => void unlockLogin.run()}>
                      Blokni ochish
                    </Button>
                  )}
                </dd>
              </div>
            )}
          </dl>
        ) : (
          <Alert tone="warning">Login berilmagan — restoranning yangi qurilmalari kira olmaydi.</Alert>
        )}
        <p className="cn-muted" style={{ marginBottom: 0 }}>
          Qurilma app.onepos.uz da shu login bilan kiradi, keyin ega yoki menejer PIN bilan tasdiqlaydi. 5 marta xato parol — 15 daqiqa blok.
        </p>
      </Card>

      <Card title="Xodimlar: PIN, parol va bloklar" className="cn-table-card">
        {(act.error || unlockLogin.error) && <Alert tone="danger">{act.error ?? unlockLogin.error}</Alert>}
        {info.staff.length === 0 ? (
          <EmptyState title="Xodim yo‘q" />
        ) : (
          <table className="cn-table">
            <thead>
              <tr>
                <th>Xodim</th>
                <th>Parol</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {info.staff.map((s) => {
                const blocked = Boolean(s.password_locked_until || s.pin_locked_until);
                return (
                  <tr key={s.id} className={s.is_active ? '' : 'ow-muted-row'}>
                    <td data-label="Xodim">
                      <span className="cn-table__name">{s.full_name}</span>
                      <span className="cn-table__sub">{s.role_name}</span>
                    </td>
                    <td data-label="Parol">
                      {s.password_locked_until ? (
                        <Badge tone="danger">{fmtDateTime(s.password_locked_until)} gacha bloklangan</Badge>
                      ) : s.pin_locked_until ? (
                        <Badge tone="danger">PIN bloklangan</Badge>
                      ) : s.has_password ? (
                        <Badge tone="success">bor</Badge>
                      ) : s.password_required ? (
                        <Badge tone="warning">majburiy, hali yo‘q</Badge>
                      ) : (
                        <span className="cn-muted">yo‘q</span>
                      )}
                    </td>
                    <td className="cn-num">
                      {canEdit && blocked && (
                        <Button size="sm" variant="ghost" disabled={act.busy} onClick={() => void act.run(s.id, 'unlock')}>
                          Blokni ochish
                        </Button>
                      )}
                      {canEdit && s.has_password && (
                        <Button size="sm" variant="ghost" onClick={() => setClearFor(s)}>
                          Parolni tozalash
                        </Button>
                      )}
                      {canEdit && s.is_active && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => {
                            setNewPin(null);
                            setPinFor(s);
                          }}
                        >
                          PIN tiklash
                        </Button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </Card>

      <Modal
        open={clearFor !== null}
        onClose={() => setClearFor(null)}
        busy={act.busy}
        title="Parolni tozalash"
        footer={
          <>
            <Button onClick={() => setClearFor(null)} disabled={act.busy}>
              Bekor qilish
            </Button>
            <Button variant="danger" disabled={act.busy} onClick={() => clearFor && void act.run(clearFor.id, 'clear')}>
              Tozalash
            </Button>
          </>
        }
      >
        <p style={{ margin: 0 }}>
          <b>{clearFor?.full_name}</b> parolini unutgan bo‘lsa. Ochiq sessiyalari yopiladi; keyingi kirishda (PIN’dan keyin)
          {clearFor?.password_required ? ' yangi parol o‘rnatadi.' : ' faqat PIN bilan kiradi.'} Egani telefon orqali tanib oling.
        </p>
      </Modal>

      <Modal
        open={pinFor !== null}
        onClose={() => setPinFor(null)}
        busy={pinAct.busy}
        title={`${pinFor?.full_name ?? ''} — yangi PIN`}
        footer={
          newPin ? (
            <Button variant="primary" onClick={() => setPinFor(null)}>
              Tayyor
            </Button>
          ) : (
            <>
              <Button onClick={() => setPinFor(null)} disabled={pinAct.busy}>
                Bekor qilish
              </Button>
              <Button variant="danger" disabled={pinAct.busy} onClick={() => pinFor && void pinAct.run(pinFor.id)}>
                Yangi PIN berish
              </Button>
            </>
          )
        }
      >
        {newPin ? (
          <>
            <Secret label="Yangi PIN" value={newPin} hint="Faqat hozir ko‘rsatiladi" />
            <p className="cn-muted" style={{ margin: 0 }}>Eski PIN va ochiq sessiyalar bekor qilindi. PIN’ni xodimga shaxsan ayting.</p>
          </>
        ) : (
          <p style={{ margin: 0 }}>
            PIN’ni unutgan bo‘lsa (ayniqsa ega — unda boshqa kirish yo‘li yo‘q). Avval odamni telefon orqali tanib oling. Eski PIN darhol
            ishlamay qoladi.
          </p>
        )}
        {pinAct.error && <Alert tone="danger">{pinAct.error}</Alert>}
      </Modal>

      {editOpen && (
        <LoginModal
          tenantId={data.tenant.id}
          current={info.login}
          onClose={() => setEditOpen(false)}
          onDone={reload}
        />
      )}
    </>
  );
}

function LoginModal({ tenantId, current, onClose, onDone }: { tenantId: string; current: string | null; onClose: () => void; onDone: () => void }) {
  const [login, setLogin] = useState(current ?? '');
  const [password, setPassword] = useState(() => (current ? '' : readablePassword()));
  const [saved, setSaved] = useState<{ login: string; password: string | null } | null>(null);
  const save = useAction(async () => {
    const pw = password.trim() === '' ? null : password;
    await api.rpc('platform_tenant_login_set', { p_tenant: tenantId, p_login: login.trim().toLowerCase(), p_password: pw });
    setSaved({ login: login.trim().toLowerCase(), password: pw });
    onDone();
  });
  return (
    <Modal
      open
      title={current ? 'Restoran logini' : 'Login berish'}
      onClose={onClose}
      busy={save.busy}
      footer={
        saved ? (
          <Button variant="primary" onClick={onClose}>
            Tayyor
          </Button>
        ) : (
          <>
            <Button onClick={onClose} disabled={save.busy}>
              Bekor qilish
            </Button>
            <Button variant="primary" onClick={() => void save.run()} disabled={save.busy || login.trim().length < 3 || (!current && password.length < 8)}>
              Saqlash
            </Button>
          </>
        )
      }
    >
      {saved ? (
        <>
          <Alert tone="success">Saqlandi. Ulangan qurilmalar ishlashda davom etadi.</Alert>
          <Secret label="Restoran logini" value={saved.login} />
          {saved.password && <Secret label="Restoran paroli" value={saved.password} />}
        </>
      ) : (
        <>
          <TextField label="Login" value={login} onChange={(e) => setLogin(e.target.value)} hint="3–32 belgi: lotin harflari, raqam, “-” yoki “_”" spellCheck={false} />
          <TextField
            label={current ? 'Yangi parol (bo‘sh qolsa — o‘zgarmaydi)' : 'Parol'}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            hint="Kamida 8 belgi"
            autoComplete="new-password"
          />
          <Button size="sm" variant="ghost" onClick={() => setPassword(readablePassword())}>
            <Icon name="refresh" /> Parol yaratish
          </Button>
          {save.error && <Alert tone="danger">{save.error}</Alert>}
        </>
      )}
    </Modal>
  );
}
